import { useEffect, useRef, useState } from "react";
import { X, Twitter, Linkedin, Copy, Check, Sparkles, Download } from "lucide-react";
import { toast } from "sonner";

// Reddit "snoo" icon — inline SVG since lucide's Reddit icon requires their
// paid tier. Keeps the mark on-brand and crisp at any size.
function RedditIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12 2C6.48 2 2 6.03 2 11c0 2.35 1.06 4.5 2.79 6.06-.14.5-.55 1.68-.63 1.94 0 0-.02.08.03.11.04.03.09.02.09.02.34-.05 1.71-.22 2.34-.29 1.6.72 3.44 1.16 5.38 1.16 5.52 0 10-4.03 10-9s-4.48-9-10-9zm-4.5 8c.83 0 1.5.67 1.5 1.5S8.33 13 7.5 13 6 12.33 6 11.5 6.67 10 7.5 10zm9 0c.83 0 1.5.67 1.5 1.5S17.33 13 16.5 13 15 12.33 15 11.5s.67-1.5 1.5-1.5zm-8.83 4.86a.5.5 0 0 1 .7-.03c1.14 1.03 2.79 1.62 4.63 1.62s3.49-.59 4.63-1.62a.5.5 0 1 1 .68.73C16.87 16.75 14.9 17.5 12 17.5s-4.87-.75-6.31-1.94a.5.5 0 0 1-.02-.7z" />
    </svg>
  );
}

/**
 * Post-claim celebration + share modal. Turns every free-launch win into a
 * viral loop by generating a shareable image + pre-filled X / LinkedIn text.
 */
export default function ShareModal({ open, onClose, block }) {
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [imgDataUrl, setImgDataUrl] = useState("");

  const shareUrl = block ? `${window.location.origin}/?claimed=${block.block_id || block.id}` : "";
  // Verbatim viral template per product spec. Includes {url} substitution
  // hint but we inline the actual shareUrl so posts include the referral link.
  const shareTemplate =
    "Just claimed my permanent piece of web real estate on the pixel-empires.com grid! Get your startup on the map before the blocks sell out.";
  const tweetText = encodeURIComponent(`${shareTemplate}\n\n${shareUrl}`);
  const linkedInText = encodeURIComponent(
    `${shareTemplate} ${shareUrl}`,
  );
  const redditTitle = encodeURIComponent(
    `${block?.brand_name ? block.brand_name + " — " : ""}${shareTemplate}`,
  );

  // Compose a shareable card as PNG via canvas so the user can save/attach it.
  useEffect(() => {
    if (!open || !block) return;
    const c = document.createElement("canvas");
    c.width = 1200; c.height = 630;
    const ctx = c.getContext("2d");
    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, 1200, 630);
    grad.addColorStop(0, "#070709");
    grad.addColorStop(1, "#14141d");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 1200, 630);
    // Neon orb
    const rg = ctx.createRadialGradient(950, 100, 20, 950, 100, 400);
    rg.addColorStop(0, "rgba(139,92,246,0.55)");
    rg.addColorStop(1, "rgba(139,92,246,0)");
    ctx.fillStyle = rg;
    ctx.fillRect(0, 0, 1200, 630);
    const rg2 = ctx.createRadialGradient(150, 520, 10, 150, 520, 350);
    rg2.addColorStop(0, "rgba(6,182,212,0.5)");
    rg2.addColorStop(1, "rgba(6,182,212,0)");
    ctx.fillStyle = rg2;
    ctx.fillRect(0, 0, 1200, 630);
    // Header
    ctx.fillStyle = "#8b5cf6";
    ctx.fillRect(80, 80, 6, 40);
    ctx.fillStyle = "#f5f6fa";
    ctx.font = "600 22px 'Manrope', sans-serif";
    ctx.fillText("PIXELEMPIRE AI · LIVE ON THE GRID", 105, 108);
    // Brand headline
    ctx.font = "700 78px 'Manrope', sans-serif";
    ctx.fillStyle = "#ffffff";
    const brand = (block.brand_name || "Your brand").slice(0, 22);
    ctx.fillText(brand, 80, 230);
    // Slogan
    if (block.hover_slogan) {
      ctx.font = "500 30px 'Manrope', sans-serif";
      ctx.fillStyle = "#9ca3af";
      ctx.fillText(block.hover_slogan.slice(0, 60), 80, 285);
    }
    // Coordinates + tier
    ctx.font = "500 20px 'JetBrains Mono', monospace";
    ctx.fillStyle = "#67e8f9";
    ctx.fillText(
      `(${block.x}, ${block.y}) · ${block.width}×${block.height} units · ${block.tier || "standard"}${block.is_free ? " · LAUNCH FREE" : ""}`,
      80, 335,
    );
    // Logo box
    const boxX = 80, boxY = 380, boxW = 180, boxH = 180;
    ctx.fillStyle = "rgba(255,255,255,0.04)";
    ctx.fillRect(boxX, boxY, boxW, boxH);
    ctx.strokeStyle = "rgba(139,92,246,0.5)";
    ctx.lineWidth = 2;
    ctx.strokeRect(boxX, boxY, boxW, boxH);
    // CTA
    ctx.font = "700 34px 'Manrope', sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.fillText("Claim your free block", 300, 445);
    ctx.font = "500 22px 'Manrope', sans-serif";
    ctx.fillStyle = "#9ca3af";
    ctx.fillText("pixelempire.ai — first 50 blocks are $0", 300, 480);
    ctx.font = "700 22px 'JetBrains Mono', monospace";
    ctx.fillStyle = "#a78bfa";
    ctx.fillText("→ pixelempire.ai", 300, 545);

    // Overlay logo if provided
    if (block.image_url) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, boxX + 8, boxY + 8, boxW - 16, boxH - 16);
        setImgDataUrl(c.toDataURL("image/png", 0.85));
      };
      img.onerror = () => setImgDataUrl(c.toDataURL("image/png", 0.85));
      img.src = block.image_url;
    } else {
      setImgDataUrl(c.toDataURL("image/png", 0.85));
    }
  }, [open, block]);

  if (!open || !block) return null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Couldn't copy — long-press the link instead"); }
  };

  const downloadImage = () => {
    if (!imgDataUrl) return;
    const a = document.createElement("a");
    a.href = imgDataUrl;
    a.download = `pixelempire-${block.brand_name || "block"}.png`;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4" data-testid="share-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="glass-strong relative max-w-lg w-full p-7 animate-fade-up">
        <button data-testid="share-close" onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/5">
          <X className="w-4 h-4" />
        </button>

        <div className="pill">
          <Sparkles className="w-3.5 h-3.5" />
          <span>You&apos;re on the grid</span>
        </div>
        <h2 className="mt-2 font-display text-4xl leading-none">
          Now go <span className="text-gradient">tell the world.</span>
        </h2>
        <p className="mt-2 text-sm text-[var(--fg-muted)]">
          Every share drives another advertiser to the grid — help us burn through the free promo.
        </p>

        {imgDataUrl && (
          <img
            ref={cardRef}
            src={imgDataUrl}
            alt="Share card"
            className="mt-5 w-full rounded-xl border border-[var(--line-strong)]"
            data-testid="share-card-image"
          />
        )}

        <div className="mt-5 grid grid-cols-2 gap-2">
          <a
            data-testid="share-twitter"
            href={`https://twitter.com/intent/tweet?text=${tweetText}`}
            target="_blank" rel="noopener noreferrer"
            className="btn-ghost justify-center"
          >
            <Twitter className="w-4 h-4" /> Share on X
          </a>
          <a
            data-testid="share-reddit"
            href={`https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${redditTitle}`}
            target="_blank" rel="noopener noreferrer"
            className="btn-ghost justify-center"
          >
            <RedditIcon className="w-4 h-4" /> Share on Reddit
          </a>
          <a
            data-testid="share-linkedin"
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&summary=${linkedInText}`}
            target="_blank" rel="noopener noreferrer"
            className="btn-ghost justify-center"
          >
            <Linkedin className="w-4 h-4" /> Share on LinkedIn
          </a>
          <button
            data-testid="share-copy"
            onClick={copyLink}
            className="btn-ghost justify-center"
          >
            {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy link</>}
          </button>
        </div>
        <button
          data-testid="share-download"
          onClick={downloadImage}
          className="btn-primary justify-center w-full mt-2"
          disabled={!imgDataUrl}
        >
          <Download className="w-4 h-4" /> Download Empire Plot card
        </button>

        <p className="mt-4 text-xs text-[var(--fg-muted)] text-center">
          Tip: post the image + link — engagement is 3× higher on X.
        </p>
      </div>
    </div>
  );
}
